import { useEffect, useState } from "react";
import { useCopyToClipboard } from "react-use";
import { Close, ContentCopy } from "@mui/icons-material";
import {
  Dialog,
  DialogContentText,
  DialogContent,
  DialogTitle,
  Button,
  Select,
  MenuItem,
  IconButton,
  Box,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  DialogActions,
} from "@mui/material";
import useCollaborators from "datasets/hooks/useCollaborators";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { uniqBy } from "ramda";

interface RequestAccessModalProps {
  open: boolean;
  onClose: () => void;
}

const RequestAccessModal = (props: RequestAccessModalProps) => {
  const [successMessage, setSuccessMessage] = useState("");
  const [, copy] = useCopyToClipboard();
  const { data: dataset } = useDataset();
  const { data: collaborators, isLoading } = useCollaborators();
  const ownerUsers =
    collaborators?.users?.filter((user) => user.access_level === DatasetAccessLevel.OWNER) || [];
  const ownerUsersFromGroups =
    collaborators?.groups
      ?.filter((group) => group.access_level === DatasetAccessLevel.OWNER)
      .flatMap((group) => group.users) || [];
  const allOwnerUsers = [...ownerUsers, ...ownerUsersFromGroups];
  const uniqueOwnerUsers = uniqBy((user) => user.email, allOwnerUsers);
  const ownerOptions = uniqueOwnerUsers.map((collaborator) => ({
    label: collaborator.full_name || collaborator.email,
    value: collaborator.email,
  }));

  const [selectedOwner, setSelectedOwner] = useState<string>(ownerOptions?.[0]?.value ?? "");

  useEffect(() => {
    if (ownerOptions && ownerOptions.length > 0 && !selectedOwner) {
      setSelectedOwner(ownerOptions?.[0]?.value ?? "");
    }
  }, [ownerOptions, selectedOwner]);

  const handleCopyEmail = () => {
    copy(selectedOwner);
    setSuccessMessage("Email copied to clipboard");
  };
  return (
    <>
      <Dialog open={props.open} onClose={props.onClose}>
        <DialogTitle sx={{ p: 3 }}>Request Access</DialogTitle>
        <IconButton
          onClick={props.onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 20,
          }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ py: 0 }}>
          <DialogContentText>
            You have preview access to this dataset. To unlock file browsing, additional tables, and
            enhanced analysis features, upgrade to full access.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            Contact a Dataset Owner below to request an upgrade.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, mb: 1, color: "black", fontWeight: 500, fontSize: 14 }}>
            Select a Dataset Owner
          </DialogContentText>
          {isLoading ? (
            <Stack pt={1} alignItems="center">
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <>
              {ownerOptions?.length === 0 && (
                <Alert sx={{ mb: 1 }} severity="error">
                  No owners found for this dataset.
                </Alert>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Select
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  disabled={!ownerOptions || ownerOptions.length === 0}
                >
                  {ownerOptions?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>

                <Tooltip title="Copy email address" placement="right">
                  <IconButton
                    onClick={handleCopyEmail}
                    disabled={!selectedOwner}
                    size="large"
                    sx={{
                      height: "42px",
                      width: "42px",
                      color: "grey.600",
                      "&:hover": {
                        color: "grey.700",
                      },
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            fullWidth
            color="primary"
            href={`mailto:${selectedOwner}?subject=Upgrade Access Request for ${
              dataset?.title ?? "dataset"
            }&body=${encodeURIComponent(`Hi, I would like to request "Analyze" permissions to the dataset ${
              dataset?.title ? `"${dataset.title}"` : `id: ${dataset?.id}`
            }.

Thank you!`)}`}
            disabled={!selectedOwner}
            target="_blank"
            rel="noopener noreferrer"
            onClick={props.onClose}
            sx={{ mt: 3 }}
          >
            Request
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={2000}
        onClose={() => setSuccessMessage("")}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>
    </>
  );
};

export default RequestAccessModal;
